import React from "react";
import { COMPANY_CONFIG } from "../data/companyConfig";
import { Zap, CheckCircle2, Building2, Landmark } from "lucide-react";

export const AboutSection = ({ onContactClick, onServicesClick }) => {
    return (
        <section
            id="about-section"
            className="py-16 sm:py-24 bg-white text-slate-900 border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header matching PaySwitch */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
                        About Us
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight font-['Outfit',sans-serif]">
                        About Global Bizness
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
                        A trusted payment technology service provider dedicated to
                        simplifying financial transactions and delivering convenience to
                        individuals and businesses across India.
                    </p>
                </div>

                {/* 3 Core Pillars: Our Story, Our Mission, Key Aspects & Compliance (White, Blue, Green) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                    {/* Pillar 1: Our Story */}
                    <div className="bg-white p-8 rounded-3xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all shadow-xs space-y-4 flex flex-col justify-between">
                        <div className="space-y-4">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center">
                                <Building2 className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 font-['Outfit',sans-serif]">
                                Our Story
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Global Bizness was established with the core vision of
                                simplifying financial transactions and bringing digital
                                convenience to every household, shopkeeper, and enterprise
                                across India through strategic business partnerships and robust
                                payment technology.
                            </p>
                        </div>
                        <ul className="space-y-2 pt-4 border-t border-slate-100 text-xs text-slate-700">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>Headquartered in Madhapur, Hyderabad</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>Reliable high-volume payment distribution</span>
                            </li>
                        </ul>
                    </div>

                    {/* Pillar 2: Our Mission (Highlighted with soft emerald tint) */}
                    <div className="bg-emerald-50/40 p-8 rounded-3xl border border-emerald-200 hover:border-emerald-300 hover:shadow-md transition-all shadow-xs space-y-4 flex flex-col justify-between relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-400/10 rounded-full blur-xl pointer-events-none" />
                        <div className="space-y-4">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                                <Zap className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 font-['Outfit',sans-serif]">
                                Our Mission
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                To create an accessible, seamless, and efficient payment
                                ecosystem for utility bill payments through innovation, trust,
                                and a strong offline agent network. We empower businesses and
                                individuals with simplified and secure payment solutions to
                                drive financial inclusion.
                            </p>
                        </div>
                        <ul className="space-y-2 pt-4 border-t border-emerald-100 text-xs text-slate-700">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span>Empowering local retailers to earn &amp; serve</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span>Zero-friction bill settlements &amp; recharges</span>
                            </li>
                        </ul>
                    </div>

                    {/* Pillar 3: Key Aspects & Compliance */}
                    <div className="bg-white p-8 rounded-3xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all shadow-xs space-y-4 flex flex-col justify-between">
                        <div className="space-y-4">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center">
                                <Landmark className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 font-['Outfit',sans-serif]">
                                Banking &amp; Compliance
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Global Bizness operates as a payment technology service
                                provider. Merchant onboarding, payment collection, and fund
                                settlement are conducted through RBI-authorised banking
                                institutions and partners in full compliance with regulations,
                                utilizing 256-bit bank-grade encryption.
                            </p>
                        </div>
                        <ul className="space-y-2 pt-4 border-t border-slate-100 text-xs text-slate-700">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>RBI-compliant partner rails</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>256-Bit SSL encryption standards</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Quick Contact & Action Ribbon (Deep Blue to Emerald Gradient) */}
                <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-emerald-900 text-white rounded-3xl p-8 sm:p-10 border border-blue-900 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="space-y-2 text-center md:text-left">
                        <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-blue-500/20 text-emerald-300 text-[11px] font-bold uppercase tracking-wider">
                            Partner With Us
                        </div>
                        <h4 className="text-xl sm:text-2xl font-extrabold text-white font-['Outfit',sans-serif]">
                            Ready to Join the Global Bizness Network?
                        </h4>
                        <p className="text-xs sm:text-sm text-blue-100 max-w-xl">
                            Connect with our corporate desk at{" "}
                            <span className="text-emerald-300 font-semibold">
                                {COMPANY_CONFIG.phone}
                            </span>{" "}
                            or email us at{" "}
                            <span className="text-emerald-300 font-semibold">
                                {COMPANY_CONFIG.email}
                            </span>
                            .
                        </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={onContactClick}
                            className="px-6 py-3 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-md shadow-emerald-600/30 cursor-pointer"
                        >
                            Contact Us
                        </button>
                        <button
                            onClick={onServicesClick}
                            className="px-6 py-3 rounded-xl font-bold text-xs bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors cursor-pointer"
                        >
                            Explore Services
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};
